// Per-type configuration for content sections (non-product homepage sections). Persisted as one
// opaque JSON string on IStorefrontSection.contentConfig — the backend stores/returns it verbatim
// (never parses it), same pattern as bannerOverlayConfig / homePageConfig. The JSON shape depends on
// the section's `type`; each type has its own parse helper below.

import { ISearchElementStyle } from '../searchPageConfig';

// The content section types. Product sections (featured/bundles/etc.) do not use contentConfig.
// (Testimonials was removed as a distinct type — a rich text column covers the need.)
//
// ASYMMETRY: 'singleProduct' is a content type on the ADMIN side only. Here it gates the editor
// (per-type form + preview dispatch) and, like the other content types, hides the product-query
// fields. The API deliberately does NOT treat singleProduct as a content type — the server resolves
// its itemType/itemId (stored in contentConfig) into the section's product(s) at serve time, so on
// the shopper it behaves like a product section, not an opaque content block.
export type ContentSectionType = 'video' | 'richText' | 'banner' | 'singleProduct' | 'divider';

export const CONTENT_SECTION_TYPES: ContentSectionType[] = ['video', 'richText', 'banner', 'singleProduct', 'divider'];

export const isContentSectionType = (type?: string): type is ContentSectionType =>
    !!type && (CONTENT_SECTION_TYPES as string[]).includes(type);

// --- video ---
export interface IVideoContentConfig {
    // A YouTube watch/share/embed URL, or a direct video file URL (.mp4 / .webm).
    url?: string;
    // No per-video title: the section title covers labeling.
}

// --- richText ---
// Rich text sections store their CONTENT as 1–3 typed column cells in the section's headerCells
// (same mechanism as product-section header/footer cells). `markdown` is legacy-only: a section
// saved with the old single-markdown form is migrated into a text cell when edited.
//
// The optional CTA button lives here (not in a cell) because contentConfig commits immediately —
// the cells go through the panel's temp-state/commit-on-close dance, which a button config has no
// reason to inherit — and because every other content type already configures itself this way.
export interface IRichTextButtonConfig {
    label?: string;                       // button renders ONLY when label is non-empty
    url?: string;                         // internal path or absolute URL
    position?: 'start' | 'end';           // default 'end' (after the content columns)
    align?: 'left' | 'center' | 'right';  // default 'left'
    style?: ISearchElementStyle;          // may carry $styleRef -> named button preset
}

export interface IRichTextContentConfig {
    markdown?: string;
    button?: IRichTextButtonConfig;
}

// --- banner (non-carousel content banner block) ---
export interface IContentBannerEntry {
    imageUrl?: string;
    linkUrl?: string;
    altText?: string;
    backgroundColor?: string;
    // The existing IBannerOverlayConfig JSON (see bannerOverlayConfig.ts) — overlay title/subtitle/
    // button/box/position. Reuses the banner overlay editor.
    overlayConfig?: string;
    // Retained so the reused overlay editor's Enabled toggle round-trips; the shopper may honor it.
    disabled?: boolean;
}
export interface IBannerContentConfig {
    banners?: IContentBannerEntry[];
    // Box height in px (falls back to a sensible default in the preview / shopper).
    height?: number;
    // When true the banner block spans the full viewport width on the storefront.
    fullBleed?: boolean;
    // How slide images fill the box: 'fill' crops (object-fit: cover, the default), 'fit'
    // letterboxes (object-fit: contain, backgroundColor shows through the bars).
    imageFit?: 'fit' | 'fill';
}

// The object-fit a banner section's slides render with, shared by the editor preview and mirrored
// by the shopper (BannerSection.tsx). Default is fill/crop.
export const bannerObjectFit = (config: IBannerContentConfig): 'cover' | 'contain' =>
    config.imageFit === 'fit' ? 'contain' : 'cover';

// --- singleProduct ---
// The merchant picks ONE item (a product, bundle, or saved-search product) from their storefront to
// feature as a hero card. Only itemType/itemId (+ optional blurb/ctaLabel) are meaningful to the
// server, which resolves the live item at serve time. `snapshot` is an editor-preview display cache
// written at pick time so the editor renders the hero without a live product fetch; the shopper
// ignores it (the server resolves the live item).
export interface ISingleProductContentConfig {
    itemType?: 'product' | 'bundle' | 'savedSearch';
    itemId?: string;      // product id / bundle id / saved-search id
    blurb?: string;       // optional merchant marketing text shown in the hero
    ctaLabel?: string;    // CTA button text, default "View Details"
    snapshot?: { name?: string; imageUrl?: string; price?: number; manufacturer?: string; hidePrice?: boolean };
}

// --- divider ---
// A horizontal rule / spacer. A 'none' line style renders just vertical space (an invisible spacer).
// Divider sections default hideTitle=true (no header band) when created.
export interface IDividerContentConfig {
    lineStyle?: 'solid' | 'dashed' | 'none';
    thickness?: number;    // line thickness in px
    color?: string;        // line color; unset = subtle neutral rgba(0,0,0,0.15) (matches shopper fallback)
    widthPercent?: number; // line width as a percent of the section (1–100), centered
    spacing?: number;      // vertical padding above and below the line, in px
}

// Generic defensive parse: an absent/invalid string yields an empty config of the right shape.
const safeParse = <T extends object>(raw?: string): T => {
    if (!raw) return {} as T;
    try {
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object' ? parsed : {}) as T;
    } catch {
        return {} as T;
    }
};

export const parseVideoConfig = (raw?: string): IVideoContentConfig => safeParse<IVideoContentConfig>(raw);
export const parseRichTextConfig = (raw?: string): IRichTextContentConfig => safeParse<IRichTextContentConfig>(raw);
export const parseBannerContentConfig = (raw?: string): IBannerContentConfig => safeParse<IBannerContentConfig>(raw);
export const parseSingleProductConfig = (raw?: string): ISingleProductContentConfig => safeParse<ISingleProductContentConfig>(raw);
export const parseDividerConfig = (raw?: string): IDividerContentConfig => safeParse<IDividerContentConfig>(raw);

// Divider preview/shopper defaults when the config leaves a field unset.
export const DIVIDER_DEFAULTS = { lineStyle: 'solid' as const, thickness: 2, widthPercent: 80, spacing: 24 };

// The section already exists, so its contentConfig field is always written (no "undefined when
// empty" collapsing like the banner overlay — an empty content section is still a valid section).
export const serializeContentConfig = (config: object): string => JSON.stringify(config);

// Extract a YouTube video id from the common URL forms (watch?v=, youtu.be/, /embed/, /shorts/).
// Returns null when the URL isn't recognizably YouTube — the caller then treats it as a direct file.
export function youTubeId(url?: string): string | null {
    if (!url) return null;
    const patterns = [
        /(?:youtube\.com\/watch\?[^#]*\bv=)([\w-]{11})/i,
        /(?:youtu\.be\/)([\w-]{11})/i,
        /(?:youtube\.com\/embed\/)([\w-]{11})/i,
        /(?:youtube\.com\/shorts\/)([\w-]{11})/i,
    ];
    for (const re of patterns) {
        const m = re.exec(url);
        if (m) return m[1];
    }
    return null;
}

// The default preview/shopper height for a content banner block when none is configured.
export const DEFAULT_CONTENT_BANNER_HEIGHT = 320;
